// Keeps open tabs on the latest deploy.
//
// 1. Version check: every build has an ID (VUE_APP_BUILD_ID, also in
//    /version.json). At most every few minutes, when the visitor moves to
//    another page, we fetch /version.json. If a newer build is live, that
//    page change loads it with a full reload instead of in-app navigation,
//    so nobody loses what they were typing.
// 2. Missing files: after a deploy, an old tab may ask for a page chunk that
//    no longer exists. Instead of breaking, reload the page (once).

const CHECK_EVERY_MS = 5 * 60 * 1000;
const RELOAD_FLAG = "guiders.reloadedForUpdate";

let lastCheck = Date.now();
let newerBuildLive = false;
let pendingHref = null; // page being opened (for the missing-file reload)

async function isNewerBuildLive() {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!response.ok) return false;
    const { buildId } = await response.json();
    return Boolean(buildId) && buildId !== process.env.VUE_APP_BUILD_ID;
  } catch (e) {
    return false; // offline or blocked: try again later
  }
}

function reloadOnce(url) {
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG) === url) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, url);
  } catch (e) {
    // storage blocked: still reload
  }
  window.location.assign(url);
  return true;
}

export function installAppUpdates(router) {
  // Only in production builds (dev has no version.json / build ID)
  if (!process.env.VUE_APP_BUILD_ID || process.env.NODE_ENV !== "production") return;

  router.beforeEach(async (to, from, next) => {
    pendingHref = router.resolve(to).href;
    // First page load is already the latest version
    if (from.matched.length === 0) return next();

    if (!newerBuildLive && Date.now() - lastCheck > CHECK_EVERY_MS) {
      lastCheck = Date.now();
      newerBuildLive = await isNewerBuildLive();
    }

    if (newerBuildLive) {
      // Full page load of the destination = the new version
      window.location.assign(router.resolve(to).href);
      return next(false);
    }
    next();
  });

  // A page's code file is gone (deleted by a newer deploy): reload
  router.onError((error) => {
    const chunkMissing =
      /Loading( CSS)? chunk [\w-]+ failed/i.test(error && error.message) ||
      (error && error.name === "ChunkLoadError");
    if (chunkMissing) reloadOnce(pendingHref || window.location.href);
  });
}
